import apiClient from './api-client';

// API Path Constants
const API_BASE = '/masterdata/labels';
const API_PATHS = {
    BASE: API_BASE,
    BY_ID: (id: number) => `${API_BASE}/${id}`,
    BY_KEY: (key: string) => `${API_BASE}/key/${key}`,
    TRANSLATIONS: (labelId: number) => `${API_BASE}/${labelId}/translations`,
    TRANSLATION: (labelId: number, languageCode: string) => `${API_BASE}/${labelId}/translations/${languageCode}`,
    TRANSLATION_UPDATE: (translationId: number) => `${API_BASE}/translations/${translationId}`,
} as const;

export type LabelType = 'BUTTON' | 'TITLE' | 'MESSAGE' | 'ERROR' | 'ANY' | 'FOOTNOTE' | 'TOOLTIP';
export type LabelCategory = 'COLLECTION_POINT' | 'EMAIL' | 'GENERAL';

export interface LabelTranslation {
    TranslationId: number;
    LabelId: number;
    LanguageCode: string;
    Translation: string;
    CreatedBy: number;
    CreatedDate: string;
    ModifiedDate?: string;
    ModifiedBy?: number;
}

export interface BaseLabel {
    LabelId: number;
    LabelKey: string;
    LabelType: LabelType;
    Category: LabelCategory;
    LabelText: string;
    Description?: string;
    IsActive: boolean;
    IsPublished: boolean;
    CreatedBy: number;
    CreatedDate: string;
    ModifiedDate?: string;
    ModifiedBy?: number;
}

export interface PaginatedResponse<T> {
    labels: T[];
    total: number;
}

export interface PaginationParams {
    page: number;
    pageSize: number;
}


// Service methods
export const labelsService = {
    getBaseLabels: async (
        filters?: {
            category?: LabelCategory;
            labelType?: LabelType;
            isPublished?: boolean;
        },
        pagination?: PaginationParams
    ): Promise<PaginatedResponse<BaseLabel>> => {
        const queryParams = new URLSearchParams();
        
        if (filters?.category) {
            queryParams.append('category', filters.category);
        }
        if (filters?.labelType) {
            queryParams.append('labelType', filters.labelType);
        }
        if (filters?.isPublished !== undefined) {
            queryParams.append('isPublished', filters.isPublished.toString());
        }
        if (pagination) {
            queryParams.append('page', pagination.page.toString());
            queryParams.append('pageSize', pagination.pageSize.toString());
        }

        const response = await apiClient.get<{ 
            success: boolean; 
            data: BaseLabel[]; 
            pagination: {
                total: number;
                page: number;
                pageSize: number;
                totalPages: number;
            }
        }>(`${API_PATHS.BASE}?${queryParams.toString()}`);
        
        return {
            labels: response.data,
            total: response.pagination.total
        };
    },

    getBaseLabelById: async (id: number): Promise<BaseLabel | undefined> => {
        const response = await apiClient.get<{ success: boolean; data: BaseLabel }>(API_PATHS.BY_ID(id));
        return response.data;
    },

    getBaseLabelByKey: async (key: string): Promise<BaseLabel | undefined> => {
        const response = await apiClient.get<{ success: boolean; data: BaseLabel }>(API_PATHS.BY_KEY(key));
        return response.data;
    },

    createBaseLabel: async (label: Omit<BaseLabel, 'labelId' | 'createdDate'>): Promise<BaseLabel> => {
        const response = await apiClient.post<{ success: boolean; data: BaseLabel }>(API_PATHS.BASE, label);
        return response.data;
    },

    updateBaseLabel: async (id: number, updates: Partial<BaseLabel>): Promise<BaseLabel> => {
        const response = await apiClient.put<{ success: boolean; data: BaseLabel }>(API_PATHS.BY_ID(id), updates);
        return response.data;
    },

    deleteBaseLabel: async (id: number): Promise<void> => {
        await apiClient.delete<{ success: boolean; message: string }>(API_PATHS.BY_ID(id));
    },

    getTranslations: async (labelId: number): Promise<LabelTranslation[]> => {
        const response = await apiClient.get<{ success: boolean; data: LabelTranslation[] }>(
            API_PATHS.TRANSLATIONS(labelId)
        );
        return response.data;
    },

    getTranslation: async (labelId: number, languageCode: string): Promise<LabelTranslation | undefined> => {
        const response = await apiClient.get<{ success: boolean; data: LabelTranslation }>(
            API_PATHS.TRANSLATION(labelId, languageCode)
        );
        return response.data;
    },

    addTranslation: async (translation: Omit<LabelTranslation, 'TranslationId' | 'CreatedDate'>): Promise<LabelTranslation> => {
        const response = await apiClient.post<{ success: boolean; data: LabelTranslation }>(
            API_PATHS.TRANSLATIONS(translation.LabelId),
            translation
        );
        return response.data;
    },

    updateTranslation: async (labelId: number, languageCode: string, translation: string): Promise<LabelTranslation> => {
        const response = await apiClient.put<{ success: boolean; data: LabelTranslation }>(
            `${API_PATHS.BASE}/${labelId}/translations/${languageCode}`,
            { Translation: translation }
        );
        return response.data;
    },

    deleteTranslation: async (labelId: number, languageCode: string): Promise<void> => {
        await apiClient.delete<{ success: boolean; message: string }>(
            API_PATHS.TRANSLATION_UPDATE(labelId)
        );
    },

    updatePublishStatus: async (labelId: number, isPublished: boolean): Promise<BaseLabel> => {
        const response = await apiClient.patch<{ success: boolean; data: BaseLabel }>(
            `${API_PATHS.BY_ID(labelId)}/publish`,
            { isPublished }
        );
        return response.data;
    }
}; 